import { useEffect, useRef } from 'preact/hooks';

const SWIPE_THRESHOLD     = 90;    // px horizontal travel for a full swipe
const FAST_FLICK_VELOCITY = 0.5;   // px/ms — fast flick commits with less travel
const FAST_FLICK_MIN_DX   = 50;    // px — min travel even for a fast flick
const MAX_VERT_DRIFT      = 40;    // px vertical drift before cancelling
const MIN_VELOCITY        = 0.3;   // px/ms — discard very slow drags
const RUBBER_BAND         = 0.2;   // damping at boundary tabs

export interface SwipeTabsOptions {
  /** Names of visible tabs in order. */
  tabs: string[];
  /** Index of the currently active tab. */
  activeIndex: number;
  /** Called with the target index when a swipe commits. */
  onTabChange: (index: number) => void;
  /** Set false to disable all gesture detection (e.g. while in SSH terminal). */
  enabled: boolean;
  /** Pass via callback ref: `ref={setContainerEl}` on the host element. */
  containerEl: HTMLElement | null;
  /** The `.sl-tabs` bar element — used to position the indicator. */
  tabsRef: { current: HTMLElement | null };
  /** The sliding indicator element inside `.sl-tabs`. */
  indicatorRef: { current: HTMLElement | null };
  /**
   * Called on every touchmove with the constrained display offset (px) and
   * the target tab index. Use this to update the panels translateX.
   */
  onDrag?: (offset: number, targetIndex: number) => void;
  /**
   * Called on touchend. `committed` = threshold met, tab will switch.
   * Use this to snap the panels back (committed=false) or lock in (committed=true).
   */
  onDragEnd?: (committed: boolean) => void;
}

/**
 * Detects horizontal swipe gestures on the session list viewport and switches
 * the top-level tabs (Sessions → Work Items → Usage → Ops).
 *
 * Swipes work everywhere on the viewport, including on session rows.
 * Row actions (pin/delete tray) are triggered by tap, not swipe.
 *
 * Visual feedback:
 * - `onDrag` lets the caller translate the panels strip in real-time.
 * - The tab indicator inside `.sl-tabs` is updated proportionally on every
 *   touchmove via direct DOM writes (no state updates, 60 fps safe).
 */
export function useSwipeTabs({
  tabs,
  activeIndex,
  onTabChange,
  enabled,
  containerEl,
  tabsRef,
  indicatorRef,
  onDrag,
  onDragEnd,
}: SwipeTabsOptions): void {
  const startX      = useRef(0);
  const startY      = useRef(0);
  const startTime   = useRef(0);
  const cancelled   = useRef(false);
  const tracking    = useRef(false);

  // Keep a stable ref to activeIndex so touchmove closures always see current value
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;

  const tabsCountRef = useRef(tabs.length);
  tabsCountRef.current = tabs.length;

  useEffect(() => {
    const el = containerEl;
    if (!el) return;
    const safeEl = el;

    function updateIndicator(dx: number, fromIdx: number, toIdx: number) {
      const tabsEl    = tabsRef.current;
      const indicator = indicatorRef.current;
      if (!tabsEl || !indicator) return;

      const buttons = tabsEl.querySelectorAll<HTMLElement>('.sl-tab');
      const from    = buttons[fromIdx];
      const to      = buttons[toIdx];
      if (!from || !to) return;

      const tabsRect = tabsEl.getBoundingClientRect();
      const fromRect = from.getBoundingClientRect();
      const toRect   = to.getBoundingClientRect();

      // progress: 0 = at current tab, 1 = fully at target tab
      const panelWidth = safeEl.offsetWidth || 1;
      const progress   = Math.min(1, Math.abs(dx) / panelWidth);

      const left  = (fromRect.left - tabsRect.left) + (toRect.left - fromRect.left) * progress;
      const width = fromRect.width + (toRect.width - fromRect.width) * progress;

      indicator.style.transition = 'none';
      indicator.style.left  = `${left}px`;
      indicator.style.width = `${width}px`;
    }

    function onTouchStart(e: TouchEvent) {
      tracking.current     = false;
      cancelled.current    = false;

      if (!enabled) return;

      // Ignore touches on interactive elements; swipe from passive areas only
      const target = e.target as HTMLElement;
      if (target.closest('input, textarea, button, a, select, [role="textbox"], .ssh-terminal')) return;

      tracking.current  = true;
      startX.current    = e.touches[0].clientX;
      startY.current    = e.touches[0].clientY;
      startTime.current = Date.now();
    }

    function onTouchMove(e: TouchEvent) {
      if (!tracking.current || cancelled.current) return;

      const dx = e.touches[0].clientX - startX.current;
      const dy = e.touches[0].clientY - startY.current;
      const idx = activeIndexRef.current;
      const count = tabsCountRef.current;

      // Vertical drift → user is scrolling, cancel gesture
      if (Math.abs(dy) > MAX_VERT_DRIFT) {
        cancelled.current = true;
        onDrag?.(0, idx);
        return;
      }

      // Prevent page scroll when clearly horizontal
      if (Math.abs(dx) > 15 && Math.abs(dx) > Math.abs(dy)) {
        e.preventDefault();
      }

      // Determine target index
      const goingLeft = dx < 0;
      const targetIdx = goingLeft
        ? Math.min(count - 1, idx + 1)
        : Math.max(0, idx - 1);

      // Rubber-band at edges
      let displayDx = dx;
      const atStart = idx === 0 && dx > 0;
      const atEnd   = idx === count - 1 && dx < 0;
      if (atStart || atEnd) displayDx = dx * RUBBER_BAND;

      onDrag?.(displayDx, targetIdx);
      updateIndicator(displayDx, idx, targetIdx);
    }

    function onTouchEnd(e: TouchEvent) {
      if (!tracking.current || cancelled.current) {
        tracking.current = false;
        onDragEnd?.(false);
        return;
      }
      tracking.current = false;

      const dx       = e.changedTouches[0].clientX - startX.current;
      const dy       = e.changedTouches[0].clientY - startY.current;
      const dt       = Math.max(1, Date.now() - startTime.current);
      const velocity = Math.abs(dx) / dt;
      const idx      = activeIndexRef.current;
      const count    = tabsCountRef.current;

      const isFastFlick = velocity >= FAST_FLICK_VELOCITY && Math.abs(dx) >= FAST_FLICK_MIN_DX;
      const isFullSwipe = Math.abs(dx) >= SWIPE_THRESHOLD && velocity >= MIN_VELOCITY;

      if ((!isFastFlick && !isFullSwipe) || Math.abs(dy) > MAX_VERT_DRIFT) {
        onDragEnd?.(false);
        return;
      }

      if (dx > 0 && idx > 0) {
        onTabChange(idx - 1);
        onDragEnd?.(true);
      } else if (dx < 0 && idx < count - 1) {
        onTabChange(idx + 1);
        onDragEnd?.(true);
      } else {
        // At boundary — bounce back
        onDragEnd?.(false);
      }
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove',  onTouchMove,  { passive: false });
    el.addEventListener('touchend',   onTouchEnd,   { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   onTouchEnd);
    };
  // tabsRef / indicatorRef are refs — intentionally omitted from deps.
  // containerEl MUST be in deps: it changes null→element after the host component mounts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerEl, enabled, onTabChange, onDrag, onDragEnd]);
}
